import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, Tag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const TIERS = ["public", "internal", "confidential", "restricted"];
const empty = { name: "", tier: "internal", color: "#64748b", description: "" };

export default function Labels() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/labels"); setItems(data); }
    catch (err) { console.error("labels load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/labels", form); toast.success("Label created"); setOpen(false); setForm(empty); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this label?")) return;
    try { await api.delete(`/labels/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout title="Sensitivity Labels" subtitle="DATA CLASSIFICATION">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="labels-count">
          {items.length} LABELS · {TIERS.map((t) => `${items.filter((l) => l.tier === t).length} ${t}`).join(" · ")}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-label-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2">
              <Plus className="w-4 h-4" /> NEW LABEL
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Tag className="w-4 h-4 text-cyan-400" /> Create Label</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2"><L>Name</L><Input data-testid="label-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div><L>Tier</L>
                <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                  <SelectTrigger data-testid="label-tier-select" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {TIERS.map((t) => <SelectItem key={t} value={t} className="font-mono uppercase text-xs">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><L>Color</L><Input data-testid="label-color-input" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm h-10" /></div>
              <div className="col-span-2"><L>Description</L><Input data-testid="label-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-label-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="labels-list">
        {items.map((l) => (
          <div key={l.label_id} data-testid={`label-card-${l.label_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm border border-zinc-700" style={{ background: l.color }} />
                <div className="font-display text-base font-bold tracking-tight">{l.name}</div>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-cyan-500/40 text-cyan-400 bg-cyan-500/10 rounded-sm">{l.tier}</span>
            </div>
            <div className="text-xs text-zinc-400 leading-relaxed min-h-[2em]">{l.description || <span className="text-zinc-600">no description</span>}</div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
              <span className="text-[10px] font-mono text-zinc-600">{l.label_id}</span>
              {user?.role === "admin" && (
                <Button size="sm" variant="ghost" data-testid={`delete-label-${l.label_id}`} onClick={() => del(l.label_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no labels yet.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
